/**
 * Sprint-005 C6 — reminders (Web Push) state machine helpers, shared by
 * `hooks/useReminderPrefs.ts` (browser/server subscribe-unsubscribe) and
 * `hooks/useReminderOptIn.ts` (the Today gentle-prompt-card gating). Kept
 * as pure functions/localStorage accessors so the opt-in heuristic can be
 * unit-tested (`tests/reminder-opt-in.test.ts`) without mocking
 * `PushManager`/`Notification` at all.
 *
 * Zero-jargon note (design-spec-today-card.md §4): these are internal
 * variable/module names only — nothing here is ever rendered to a human.
 * The words "push"/"subscription"/"permission" stay confined to code and
 * API payloads, never UI copy (see `components/ReminderOptInCard.tsx` and
 * `views/MoreView.tsx` for the governed copy itself).
 */

const STORAGE_PREFIX = "hayloft.reminders.";
const PROMPT_DISMISSED_KEY = `${STORAGE_PREFIX}promptDismissed`;
const VISITS_KEY = `${STORAGE_PREFIX}visits`;
const COMPLETED_ONCE_KEY = `${STORAGE_PREFIX}completedOnce`;
const DEVICE_SUBSCRIPTION_ID_KEY = `${STORAGE_PREFIX}deviceSubscriptionId`;

function safeGetItem(key: string): string | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
  } catch {
    // Private-browsing/storage-quota edge cases — treat as "never set"
    // rather than throwing and breaking the Today view.
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    // Best-effort only — a lost write just means the prompt might be
    // re-offered next visit, never a functional break.
  }
}

/** True once the person has either accepted, declined, or dismissed the
 * Today opt-in card, OR successfully turned reminders on/off from the
 * More tab — any of these count as "asked and answered," so the ambush
 * card never nags again (re-offerable only from the More tab, per the
 * task brief). */
export function isPromptDismissed(): boolean {
  return safeGetItem(PROMPT_DISMISSED_KEY) === "true";
}

export function dismissPrompt(): void {
  safeSetItem(PROMPT_DISMISSED_KEY, "true");
}

/** Increments and returns this device's persisted "how many times has
 * the app been opened" counter — one half of the opt-in heuristic
 * (flagged, see `useReminderOptIn.ts`'s docstring: "first completion OR
 * second visit"). Called once per `AuthenticatedApp` mount. */
export function recordVisit(): number {
  const next = getVisitCount() + 1;
  safeSetItem(VISITS_KEY, String(next));
  return next;
}

export function getVisitCount(): number {
  const raw = safeGetItem(VISITS_KEY);
  const n = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** The other half of the heuristic: has this person completed at least
 * one task, ever, on this device. Sticky once true (never reset). */
export function recordCompletionOnce(): void {
  safeSetItem(COMPLETED_ONCE_KEY, "true");
}

export function hasCompletedOnce(): boolean {
  return safeGetItem(COMPLETED_ONCE_KEY) === "true";
}

/** This device's own `push_subscriptions.id` (server row), stashed after
 * a successful subscribe so unsubscribing later needs no extra lookup
 * round trip — see `hooks/useReminderPrefs.ts::disable`'s fallback path
 * for what happens when this is missing (storage cleared, or a
 * subscription created before this key existed). */
export function getDeviceSubscriptionId(): string | null {
  return safeGetItem(DEVICE_SUBSCRIPTION_ID_KEY);
}

export function setDeviceSubscriptionId(id: string): void {
  safeSetItem(DEVICE_SUBSCRIPTION_ID_KEY, id);
}

export function clearDeviceSubscriptionId(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(DEVICE_SUBSCRIPTION_ID_KEY);
  } catch {
    // no-op — see safeSetItem's comment.
  }
}

/**
 * The Today opt-in card's own visibility rule (task brief: "pick a
 * simple heuristic, flag it" — FLAGGED HERE): show it once this device
 * has either been opened at least twice, OR the person has completed at
 * least one task, whichever comes first — never on a person's very
 * first tap of their very first visit, but also never held back for a
 * fixed number of days (this app has no per-day visit cadence to key
 * off of). Never shown at all once the person has already answered
 * (accept/decline/dismiss, or already subscribed some other way), and
 * never shown if this browser can't support Web Push or has already
 * denied the browser-level prompt (that combination is the More tab's
 * job to explain honestly instead — see `views/MoreView.tsx`).
 */
export function shouldShowOptInPrompt(params: {
  alreadyDismissed: boolean;
  alreadySubscribed: boolean;
  canPrompt: boolean; // false for "unsupported" or browser-level "denied"; true for "needs_install"
  visitCount: number;
  completedOnce: boolean;
}): boolean {
  if (params.alreadyDismissed || params.alreadySubscribed || !params.canPrompt) return false;
  return params.visitCount >= 2 || params.completedOnce;
}

/** Feature-detects Web Push support (task brief item #4: "unsupported
 * browser (no PushManager) -> row hidden"). Checked once, defensively,
 * rather than assumed from `Notification` alone (Safari, for one
 * historical example, ships `Notification` without `PushManager`). */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

// ---------------------------------------------------------------------------
// Device detection — which phone/computer and browser this is, so the
// reminders row can give the RIGHT steps instead of a dead "unsupported".
// Pure functions over a `DeviceEnv` snapshot so the whole matrix is
// unit-testable (`tests/reminders-device.test.ts`) without faking `navigator`.
// ---------------------------------------------------------------------------

export type DevicePlatform = "ios" | "android" | "desktop";
export type DeviceBrowser = "safari" | "chrome" | "firefox" | "edge" | "other";

export interface DeviceEnv {
  userAgent: string;
  maxTouchPoints: number;
  /** iOS Safari's non-standard `navigator.standalone`. */
  navigatorStandalone: boolean;
  /** `matchMedia('(display-mode: standalone)').matches`. */
  displayModeStandalone: boolean;
}

export interface DeviceInfo {
  platform: DevicePlatform;
  browser: DeviceBrowser;
  /** `[major, minor]` on iPhone/iPad when the user agent says; else null. */
  iosVersion: [number, number] | null;
  /** Running as the installed Home Screen app, not in a browser tab. */
  standalone: boolean;
  /** What to call this device in plain copy. */
  noun: "iPhone" | "iPad" | "phone" | "computer";
}

/** Web push on iPhone/iPad needs iOS/iPadOS 16.4+ AND the Home Screen app. */
export const IOS_MIN_PUSH_VERSION: [number, number] = [16, 4];

export function readDeviceEnv(): DeviceEnv {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  let displayModeStandalone = false;
  try {
    displayModeStandalone =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    // Older engines without media-query support for display-mode.
  }
  return {
    userAgent: nav?.userAgent ?? "",
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    navigatorStandalone: (nav as { standalone?: boolean } | undefined)?.standalone === true,
    displayModeStandalone,
  };
}

export function detectDevice(env: DeviceEnv): DeviceInfo {
  const ua = env.userAgent;
  const standalone = env.navigatorStandalone || env.displayModeStandalone;
  const isIPhone = /iPhone|iPod/.test(ua);
  // iPadOS 13+ Safari asks for the desktop site by default and reports
  // itself as a Mac — the touch screen is the only reliable tell.
  const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && env.maxTouchPoints > 1);

  if (isIPhone || isIPad) {
    let browser: DeviceBrowser = "other";
    if (/CriOS\//.test(ua)) browser = "chrome";
    else if (/FxiOS\//.test(ua)) browser = "firefox";
    else if (/EdgiOS\//.test(ua)) browser = "edge";
    else if (/Version\/[\d.]+.*Safari\//.test(ua)) browser = "safari";
    else if (standalone) browser = "safari"; // the Home Screen app is Safari's engine
    const osMatch = /OS (\d+)[_.](\d+)/.exec(ua);
    const versionMatch = /Version\/(\d+)\.(\d+)/.exec(ua);
    const m = isIPhone || /iPad/.test(ua) ? (osMatch ?? versionMatch) : versionMatch;
    return {
      platform: "ios",
      browser,
      iosVersion: m ? [Number(m[1]), Number(m[2])] : null,
      standalone,
      noun: isIPad ? "iPad" : "iPhone",
    };
  }

  const isAndroid = /Android/.test(ua);
  let browser: DeviceBrowser = "other";
  if (/Edg(A|e)?\//.test(ua)) browser = "edge";
  else if (/Firefox\//.test(ua)) browser = "firefox";
  else if (/SamsungBrowser|OPR\//.test(ua)) browser = "other";
  else if (/Chrome\//.test(ua)) browser = "chrome";
  else if (/Safari\//.test(ua)) browser = "safari";
  return {
    platform: isAndroid ? "android" : "desktop",
    browser,
    iosVersion: null,
    standalone,
    noun: isAndroid ? "phone" : "computer",
  };
}

function versionBelow(v: [number, number], min: [number, number]): boolean {
  return v[0] < min[0] || (v[0] === min[0] && v[1] < min[1]);
}

/**
 * Whether this device can turn reminders on right now, and if not, what
 * the person has to do first:
 * - `needs_update` — iPhone/iPad below 16.4 (no web push at all there).
 * - `needs_install` — iPhone/iPad in a Safari/Chrome tab: push only exists
 *   in the Home Screen app.
 * - `unsupported` — any other browser with no push support.
 */
export function pushCapability(
  device: DeviceInfo,
  hasPushApis: boolean,
): "supported" | "unsupported" | "needs_install" | "needs_update" {
  if (device.platform === "ios") {
    if (device.iosVersion && versionBelow(device.iosVersion, IOS_MIN_PUSH_VERSION)) {
      return "needs_update";
    }
    if (!device.standalone) return "needs_install";
    // Installed but still no push — the iOS version is too old even if the
    // user agent didn't say so.
    return hasPushApis ? "supported" : "needs_update";
  }
  return hasPushApis ? "supported" : "unsupported";
}

/**
 * Converts a base64url-encoded VAPID public key (the exact string
 * `GET /api/v1/push/vapid-public-key` returns) into the `Uint8Array`
 * `PushManager.subscribe({applicationServerKey})` requires. Standard,
 * widely-used conversion (no library needed for one function).
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}
